import type { Notification, NotificationType } from "../types/notification.types";

const notificationDefaults: Notification = {
  id: 0,
  active: undefined,
  url: undefined,
  created_at: undefined,
  modified_at: undefined,
  user: undefined,
  user_name: undefined,
  verb: "",
  description: "",
  is_read: false,
  target: undefined,
};

export const createNotification = <T = any>(data: Partial<Notification<T>> = {}): Notification<T> => ({
  ...notificationDefaults,
  ...data,
}) as Notification<T>;

export const hydrateNotification = <T = any>(data: Partial<Notification<T>>): Notification<T> =>
  createNotification(data);

export const markNotificationAsRead = <T extends NotificationType>(notification: T): T => ({
  ...notification,
  is_read: true,
});

export const markNotificationAsUnread = <T extends NotificationType>(notification: T): T => ({
  ...notification,
  is_read: false,
});

export const isNotificationUnread = (notification: NotificationType): boolean => {
  return !notification.is_read;
};

export const hasValidTarget = (notification: NotificationType): boolean => {
  return notification.target !== undefined && notification.target !== null;
};

export const getNotificationTarget = <T = any, K = T>(
  notification: NotificationType
): K | undefined => {
  return notification.target as K | undefined;
};

export const markMultipleAsRead = <T extends NotificationType>(
  notifications: T[],
  notificationIds: number[]
): T[] => {
  return notifications.map(notification =>
    notificationIds.includes(notification.id) ? markNotificationAsRead(notification) : notification
  );
};

export const markAllNotificationsAsRead = <T extends NotificationType>(notifications: T[]): T[] => {
  return notifications.map(notification => markNotificationAsRead(notification));
};

export const countUnreadNotifications = (notifications: NotificationType[]): number => {
  return notifications.filter(notification => isNotificationUnread(notification)).length;
};

export const getUnreadNotifications = <T extends NotificationType>(notifications: T[]): T[] => {
  return notifications.filter(notification => isNotificationUnread(notification));
};

export const getReadNotifications = <T extends NotificationType>(notifications: T[]): T[] => {
  return notifications.filter(notification => !isNotificationUnread(notification));
};
